import { useEffect, useMemo, useState } from 'react';
import { RequestStatus } from '@vwt/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, AlarmClock, Inbox, Plus, Search, SlidersHorizontal, X } from 'lucide-react';
import { Badge, Button, Checkbox, cn, Drawer, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, EmptyState, Input, Tabs, TabsList, TabsTrigger, toast } from '@vwt/ui';
import { api, POLL_ACTIVE, useApiQuery } from '../../lib/api.js';
import { listParam, toggleListValue } from '../../lib/list-sort.js';
import { FilterChips, PeopleFilter, personChips, ResultCount, SortHeader, usePeopleNames, type FilterChip } from '../../components/table-tools.js';
import { useTranslation } from '../../lib/i18n.js';
import { InfiniteScrollSentinel } from '../../components/infinite-scroll.js';
import { useMe } from '../../lib/session.js';
import { hasUnsavedChanges } from '../../lib/unsaved.js';
import { KeyLabel, PriorityLabel, RequestStatusBadge } from '../../components/badges.js';
import { DateWithTime, PageHeader, QueryState, TimeAgo, UserChip } from '../../components/common.js';
import { SavedViewsMenu } from '../../components/saved-views.js';
import { RequestDetail } from './request-detail.js';
import { ExportMenu } from '../../components/export-menu.js';
import { formatDateTime } from '../../lib/format.js';

type View = 'inbox' | 'mine' | 'attention' | 'following' | 'all' | 'snoozed';

/** Filters sent to the list (and the export) besides the tab. */
const REQUEST_FILTER_KEYS = ['q', 'status', 'priority', 'assigneeId', 'authorId', 'triageTeamId', 'projectId', 'sort', 'dir'] as const;

export function RequestsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const defaultView: View = me.permissions.canTriage ? 'inbox' : 'mine';
  const view = (params.get('view') as View | null) ?? defaultView;
  const selected = params.get('selected');
  const [search, setSearch] = useState(params.get('q') ?? '');

  const set = (key: string, value: string | null) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  const filters = useMemo(() => Object.fromEntries(REQUEST_FILTER_KEYS.map((k) => [k, params.get(k) ?? undefined]).filter(([, v]) => v)) as Record<string, string>, [params]);

  useEffect(() => {
    if ((params.get('q') ?? '') === search.trim()) return;
    const id = setTimeout(() => set('q', search.trim() || null), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = useInfiniteQuery({
    queryKey: ['listRequests', 'infinite', view, filters],
    queryFn: ({ pageParam, signal }) => api.call('listRequests', { query: { view, ...filters, ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.meta.nextCursor ?? undefined,
    refetchInterval: POLL_ACTIVE,
  });
  const items = useMemo(() => query.data?.pages.flatMap((p) => p?.data ?? []) ?? [], [query.data]);
  const views: View[] = me.permissions.canTriage ? ['inbox', 'attention', 'snoozed', 'mine', 'following', 'all'] : ['mine', 'following', 'all'];
  const total = query.data?.pages[0]?.meta.total;
  const chips = useRequestChips(params, set);
  const sortable = (label: string, sortKey: string) => <SortHeader label={label} sortKey={sortKey} params={params} setParams={setParams} />;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('requests.title')}
        subtitle={t('requests.subtitle')}
        actions={
          <Button variant="primary" onClick={() => navigate('/requests/new')}>
            <Plus /> {t('nav.newRequest')}
          </Button>
        }
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <Tabs value={view} onValueChange={(v) => set('view', v === defaultView ? null : v)}>
            <TabsList className="border-b-0">
              {views.map((v) => (
                <TabsTrigger key={v} value={v}>
                  {v === 'attention' ? <AlertTriangle className="text-danger" /> : null}
                  {v === 'snoozed' ? <AlarmClock /> : null}
                  {t(`requests.tabs.${v}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="mb-2 flex w-full max-w-md items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-fg-subtle" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('nav.search')} className="pl-8" aria-label={t('nav.search')} />
            </div>
            <RequestFilterMenu params={params} setParam={set} />
            <PeopleFilter
              params={params}
              setParam={set}
              roles={[
                { param: 'assigneeId', label: t('requests.columns.assignee'), noneLabel: t('common.unassigned') },
                { param: 'authorId', label: t('requests.columns.author') },
              ]}
            />
            <ExportMenu path="/api/v1/requests/export" filters={{ view, ...filters }} />
            <SavedViewsMenu
              scope="REQUESTS"
              params={params}
              onApply={(query) => {
                setSearch(query.q ?? '');
                setParams(new URLSearchParams(query));
              }}
            />
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <FilterChips chips={chips} />
          </div>
          <ResultCount total={total} loaded={items.length} className="pt-1 pb-3" />
        </div>
      </PageHeader>
      <div className="min-h-0 flex-1 overflow-auto">
        <QueryState query={query} empty={items.length === 0 ? <EmptyState icon={<Inbox />} title={view === 'inbox' ? t('requests.emptyInbox') : t('requests.empty')} action={<Button onClick={() => navigate('/requests/new')}><Plus /> {t('nav.newRequest')}</Button>} /> : undefined}>
          <ul className="divide-y divide-border md:hidden">
            {items.map((r) => (
              <li key={r.id}>
                <button type="button" data-testid="request-row" onClick={() => set('selected', r.key)} className="flex w-full flex-col gap-1.5 bg-surface px-4 py-3 text-left">
                  <span className="flex items-center gap-2">
                    <KeyLabel value={r.key} />
                    <span className="ml-auto text-xs"><TimeAgo iso={r.updatedAt} /></span>
                  </span>
                  <span className="text-sm font-medium">{r.title || '—'}</span>
                  <span className="flex flex-wrap items-center gap-2">
                    <RequestStatusBadge status={r.status} />
                    <PriorityLabel priority={r.priority} />
                    {r.unresolvedFeedback ? <Badge tone="danger">{t('status.confirmation.PERSISTS')}</Badge> : null}
                    {r.snoozedUntil && new Date(r.snoozedUntil) > new Date() ? <Badge tone="outline" title={t('requests.snooze.badge', { time: formatDateTime(r.snoozedUntil) })}><AlarmClock /> {formatDateTime(r.snoozedUntil)}</Badge> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <table className="hidden w-full border-collapse text-sm md:table">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="border-b border-border text-left text-xs text-fg-subtle">
                <th className="px-3 py-2 font-medium">{sortable(t('requests.columns.key'), 'key')}</th>
                <th className="w-full px-3 py-2 font-medium">{sortable(t('requests.columns.title'), 'title')}</th>
                <th className="px-3 py-2 font-medium">{sortable(t('requests.columns.status'), 'status')}</th>
                <th className="hidden px-3 py-2 font-medium md:table-cell">{sortable(t('requests.columns.priority'), 'priority')}</th>
                <th className="hidden px-3 py-2 font-medium lg:table-cell">{sortable(t('requests.columns.author'), 'author')}</th>
                <th className="hidden px-3 py-2 font-medium lg:table-cell">{sortable(t('requests.columns.assignee'), 'assignee')}</th>
                <th className="hidden px-3 py-2 font-medium xl:table-cell">{t('requests.columns.team')}</th>
                <th className="hidden px-3 py-2 font-medium lg:table-cell">{sortable(t('requests.columns.created'), 'createdAt')}</th>
                <th className="px-3 py-2 font-medium">{sortable(t('requests.columns.updated'), 'updatedAt')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  data-testid="request-row"
                  tabIndex={0}
                  onClick={() => set('selected', r.key)}
                  onKeyDown={(e) => e.key === 'Enter' && set('selected', r.key)}
                  className={cn('cursor-pointer border-b border-border bg-surface hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', (selected === r.key || selected === r.id) && 'bg-accent-soft/30')}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    <KeyLabel value={r.key} />
                  </td>
                  <td className="max-w-0 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium">{r.title || '—'}</span>
                      {r.unresolvedFeedback ? <Badge tone="danger">{t('status.confirmation.PERSISTS')}</Badge> : null}
                      {r.snoozedUntil && new Date(r.snoozedUntil) > new Date() ? <Badge tone="outline" title={t('requests.snooze.badge', { time: formatDateTime(r.snoozedUntil) })}><AlarmClock /> {formatDateTime(r.snoozedUntil)}</Badge> : null}
                      {r.commentCount ? <span className="text-xs text-fg-subtle">💬 {r.commentCount}</span> : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <RequestStatusBadge status={r.status} />
                  </td>
                  <td className="hidden px-3 py-2 whitespace-nowrap md:table-cell">
                    <PriorityLabel priority={r.priority} />
                  </td>
                  <td className="hidden px-3 py-2 whitespace-nowrap lg:table-cell">
                    <UserChip user={r.author} link />
                  </td>
                  <td className="hidden px-3 py-2 whitespace-nowrap lg:table-cell">
                    <UserChip user={r.assignee} link />
                  </td>
                  <td className="hidden px-3 py-2 text-xs whitespace-nowrap text-fg-muted xl:table-cell">{r.triageTeam?.name ?? '—'}</td>
                  <td className="hidden px-3 py-2 text-xs whitespace-nowrap lg:table-cell">
                    <DateWithTime iso={r.createdAt} />
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    <TimeAgo iso={r.updatedAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <InfiniteScrollSentinel query={query} />
        </QueryState>
      </div>
      <Drawer open={!!selected} onOpenChange={(open) => {
          if (open) return;
          if (hasUnsavedChanges()) {
            toast.warning(t('common.unsavedChanges'));
            return;
          }
          set('selected', null);
        }}
        onEscapeKeyDown={(event) => {
          if (!hasUnsavedChanges()) return;
          event.preventDefault();
          toast.warning(t('common.unsavedChanges'));
        }} title={t('requests.title')} closeLabel={t('common.close')}>
        {selected ? <RequestDetail id={selected} inDrawer /> : null}
      </Drawer>
    </div>
  );
}

function RequestFilterMenu({ params, setParam }: { params: URLSearchParams; setParam: (key: string, value: string | null) => void }) {
  const { t } = useTranslation();
  const teams = useApiQuery('listTeams', { query: {} });
  const projects = useApiQuery('listProjects', { query: {} });
  const toggle = (key: string, value: string) => setParam(key, toggleListValue(params, key, value));
  const has = (key: string, value: string) => listParam(params, key).includes(value);
  const count = ['status', 'priority', 'triageTeamId', 'projectId'].reduce((n, k) => n + listParam(params, k).length, 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="md" variant="secondary" aria-label={t('common.filter')}>
          <SlidersHorizontal />
          {count ? <Badge tone="accent">{count}</Badge> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[70vh] w-64 overflow-y-auto">
        <DropdownMenuLabel>{t('requests.columns.status')}</DropdownMenuLabel>
        {RequestStatus.options
          .filter((s) => s !== 'DRAFT')
          .map((s) => (
            <DropdownMenuItem key={s} onSelect={(e) => (e.preventDefault(), toggle('status', s))}>
              <Checkbox checked={has('status', s)} aria-hidden tabIndex={-1} /> {t(`status.request.${s}`)}
            </DropdownMenuItem>
          ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('requests.columns.priority')}</DropdownMenuLabel>
        {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggle('priority', p))}>
            <Checkbox checked={has('priority', p)} aria-hidden tabIndex={-1} /> {t(`status.priority.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('requests.columns.team')}</DropdownMenuLabel>
        {(teams.data?.data ?? []).map((team) => (
          <DropdownMenuItem key={team.id} onSelect={(e) => (e.preventDefault(), toggle('triageTeamId', team.id))}>
            <Checkbox checked={has('triageTeamId', team.id)} aria-hidden tabIndex={-1} /> {team.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.project')}</DropdownMenuLabel>
        {(projects.data?.data ?? []).map((p) => (
          <DropdownMenuItem key={p.id} onSelect={(e) => (e.preventDefault(), toggle('projectId', p.id))}>
            <Checkbox checked={has('projectId', p.id)} aria-hidden tabIndex={-1} /> {p.name}
          </DropdownMenuItem>
        ))}
        {count ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => ['status', 'priority', 'triageTeamId', 'projectId'].forEach((k) => setParam(k, null))}>
              <X /> {t('common.clearFilters')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useRequestChips(params: URLSearchParams, setParam: (key: string, value: string | null) => void): FilterChip[] {
  const { t } = useTranslation();
  const teams = useApiQuery('listTeams', { query: {} });
  const projects = useApiQuery('listProjects', { query: {} });
  const names = usePeopleNames([...listParam(params, 'assigneeId'), ...listParam(params, 'authorId')]);
  const chips: FilterChip[] = [];
  const many = (param: string, role: string, label: (value: string) => string) => {
    for (const value of listParam(params, param)) {
      chips.push({
        key: `${param}:${value}`,
        label: (
          <>
            <span className="text-fg-muted">{role}:</span> {label(value)}
          </>
        ),
        onRemove: () => setParam(param, toggleListValue(params, param, value)),
      });
    }
  };
  many('status', t('requests.columns.status'), (v) => t(`status.request.${v}`));
  many('priority', t('requests.columns.priority'), (v) => t(`status.priority.${v}`));
  chips.push(...personChips(params, setParam, 'assigneeId', t('requests.columns.assignee'), names, t('common.me'), t('common.unassigned')));
  chips.push(...personChips(params, setParam, 'authorId', t('requests.columns.author'), names, t('common.me'), t('common.unassigned')));
  many('triageTeamId', t('requests.columns.team'), (v) => teams.data?.data.find((team) => team.id === v)?.name ?? '…');
  many('projectId', t('work.filters.project'), (v) => projects.data?.data.find((p) => p.id === v)?.name ?? '…');
  return chips;
}
